/**
 * Memory-graph domain model and force-directed simulation. Pure data + math:
 * it owns the nodes/edges arrays and the per-frame physics/draw, but knows
 * nothing about React. `useMemoryGraph` wires it to a canvas; `useAgentChat`
 * drives it through the returned `MemoryGraphApi`, growing + lighting up the
 * graph from the agent's real `memory-write` stream events.
 */

export type MemType = 'prompt' | 'response' | 'tool' | 'fact' | 'entity' | 'reflection';

export type MemTier = 'episodic' | 'semantic' | 'meta';
export type EdgeKind = 'derives' | 'mentions' | 'similar' | 'session';

/** A memory link is either a target id (defaults to a "derives" edge) or an
 *  explicit {to, kind} pair. */
export type MemLink = number | { to: number; kind: EdgeKind };

export interface MemNode {
  id: number;
  label: string;
  type: MemType;
  tier: MemTier;
  detail: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  born: number;
  radius: number;
  /** Grow-in factor 0→1 used to scale the node on first appearance. */
  pulse: number;
  /** Whether the memory is currently loaded into the working-context window. */
  active: boolean;
  /** Backend memory-record id, when this node was created from a real
   *  `memory-write` stream event. Absent on synthetic nodes (e.g. the
   *  per-turn prompt). Lets `delete` mutations address the right node. */
  recordId?: string;
}

export interface MemEdge {
  a: number;
  b: number;
  kind: EdgeKind;
  born: number;
}

interface TypeMeta {
  color: string;
  tier: MemTier;
  label: string;
}

export const TYPE: Record<MemType, TypeMeta> = {
  prompt: { color: '#22d3ee', tier: 'episodic', label: 'User prompt' },
  response: { color: '#60a5fa', tier: 'episodic', label: 'Agent reply' },
  tool: { color: '#facc15', tier: 'episodic', label: 'Tool result' },
  fact: { color: '#4ade80', tier: 'semantic', label: 'Semantic fact' },
  entity: { color: '#c084fc', tier: 'semantic', label: 'Entity / percept' },
  reflection: { color: '#7d8290', tier: 'meta', label: 'Reflection' },
};

const TIER_RADIUS: Record<MemTier, number> = {
  episodic: 4.5,
  semantic: 6.5,
  meta: 5.5,
};

interface EdgeStyle {
  dash: number[];
  alpha: number;
}

const EDGE: Record<EdgeKind, EdgeStyle> = {
  derives: { dash: [], alpha: 0.32 },
  mentions: { dash: [4, 3], alpha: 0.24 },
  similar: { dash: [1, 4], alpha: 0.18 },
  // Threads consecutive prompts of one session — a dotted line. Kept legible
  // when dormant (prompts go dark between turns) so the conversation's spine
  // stays visible across the whole graph, not just the in-context turn.
  session: { dash: [2, 5], alpha: 0.3 },
};

const MEM_LABEL_MAX = 28;

/** Map a backend memory-record kind to a graph node type. Turn records are
 *  episodic (rendered as the agent-reply node); facts are semantic. Shared by
 *  the turn-stream and the `/memory/events` snapshot paths. */
export function memoryNodeType(kind: 'turn' | 'fact'): MemType {
  return kind === 'fact' ? 'fact' : 'response';
}

/** Truncate a record's text down to a node label. */
export function memoryNodeLabel(text: string): string {
  return text.length > MEM_LABEL_MAX ? `${text.slice(0, MEM_LABEL_MAX)}…` : text;
}

/** Popover detail for a record node: a module + kind context line, then the
 *  record's *full* text (the node label is truncated). Turn records carry the
 *  runtime's one-line tool trace appended to their text, so showing the full
 *  text here is what makes a turn's tool calls/results visible on inspection.
 *  Shared by the turn-stream and `/memory/events` snapshot paths. */
export function memoryNodeDetail(moduleId: string, kind: string, text: string): string {
  return `${moduleId} · ${kind}\n\n${text}`;
}

export interface PopoverPlacement {
  /** Viewport-space left/top for the (position:fixed) popover. */
  left: number;
  top: number;
  /** Which side of the node it opened toward. */
  side: 'left' | 'right';
}

/**
 * Place the node-detail popover in **viewport** coordinates, opening toward
 * whichever horizontal side of the node has the most room on the page, then
 * hard-clamping fully inside the viewport so it can never be cut off. Because
 * the memory graph lives in the right-hand inspector panel, the roomier side is
 * almost always the left (the wide chat area), so popovers open leftward and —
 * as a `position:fixed` element — can extend out of the narrow panel.
 *
 * Pure + deterministic (no DOM) so it's unit-testable; `useMemoryGraph` feeds
 * it the node's on-screen position and the popover's measured size.
 *
 * @param nodeX  node centre X in viewport px
 * @param nodeY  node centre Y in viewport px
 * @param vw     viewport width  @param vh viewport height
 * @param pw     popover width    @param ph popover height
 * @param gap    offset from the node     @param margin min gap from every edge
 */
export function placePopover(
  nodeX: number,
  nodeY: number,
  vw: number,
  vh: number,
  pw: number,
  ph: number,
  gap = 14,
  margin = 6,
): PopoverPlacement {
  const spaceLeft = nodeX;
  const spaceRight = vw - nodeX;
  const side: 'left' | 'right' = spaceRight >= spaceLeft ? 'right' : 'left';
  let left = side === 'right' ? nodeX + gap : nodeX - gap - pw;
  left = Math.max(margin, Math.min(left, vw - pw - margin));
  // Prefer above the node; drop below if it would clip the top; then clamp.
  let top = nodeY - ph - 8;
  if (top < margin) {
    top = nodeY + 14;
  }
  top = Math.max(margin, Math.min(top, vh - ph - margin));
  return { left, top, side };
}

export interface GraphCounts {
  nodes: number;
  edges: number;
  active: number;
}

/** Imperative handle the console uses to grow + light up the graph from the
 *  agent's real `memory-write` stream events. */
export interface MemoryGraphApi {
  addMemory: (
    label: string,
    type: MemType,
    links?: MemLink[],
    detail?: string,
    recordId?: string,
  ) => number;
  /** Update a node's label + detail in place. Used to roll up a turn's tool
   *  calls into one aggregate node whose count grows as more calls land. */
  setNodeText: (id: number, label: string, detail: string) => void;
  setContext: (ids: number[]) => void;
  activate: (ids: number[]) => void;
  clearContext: () => void;
  /** Remove the nodes carrying these backend record ids (and their edges).
   *  Drives `memory-write` `delete` mutations. */
  removeRecords: (recordIds: readonly string[]) => void;
  /** Remove every record-backed node whose id is NOT in `keep` (synthetic nodes
   *  are untouched). Reconciles the graph to an authoritative record listing —
   *  the session-scoped `/agent-memory` sections (see `useMemoryGraphSync`). */
  retainRecords: (keep: ReadonlySet<string>) => void;
  /** Remove every node created from a real memory record. Drives `reset`. */
  clearMemoryRecords: () => void;
  /** Drop all nodes/edges and reset ids — used to (re)bootstrap a console. */
  reset: () => void;
}

// --- Force-simulation stability ---------------------------------------------
// Base pairwise repulsion strength.
const REPULSION = 900;
// Node count up to which repulsion runs at full strength. Beyond it the
// repulsion eases off ∝ 1/N so a long session's many nodes settle instead of
// over-packing the small inspector canvas and churning against its bounds.
// Small graphs (≤ this many nodes) behave exactly as before.
const REPULSION_FULL_N = 24;
// Distance² floor for the repulsion denominator (≈6px). Caps the close-range
// force at REPULSION / MIN_D2 so near-coincident nodes — which a densely packed,
// bounded canvas inevitably produces — can't inject a runaway velocity spike.
// This is the root cause of the "nodes strobing in the four corners" blow-up:
// the old code let two nodes within 0.1px generate a ~90000 px/frame impulse.
const MIN_D2 = 36;
// Per-node speed ceiling (px/frame): a hard backstop on per-frame travel so a
// momentary force spike can never teleport or strobe a node across the canvas.
const MAX_SPEED = 6;

/**
 * Holds the mutable graph state and exposes mutation + rendering primitives.
 * One instance per console mount; `useMemoryGraph` owns its lifecycle.
 */
export class MemoryGraph {
  readonly nodes: MemNode[] = [];
  readonly edges: MemEdge[] = [];
  private nextId = 0;
  // Seed with a non-degenerate default so nodes added before the canvas has a
  // real size (e.g. on mobile, where memory events stream in while the inspector
  // tab is hidden) spawn spread around a plausible centre instead of at (0,0).
  private width = 360;
  private height = 360;

  setBounds(width: number, height: number): void {
    // A hidden panel — the mobile "memory" tab while the user is on chat — reports
    // 0×0. Ignore degenerate bounds and keep the last good size: otherwise every
    // node gets clamped onto the same corner point, and coincident nodes can't
    // separate (zero repulsion direction) — the "single dot in portrait" bug.
    // Real bounds arrive from the ResizeObserver the moment the panel is shown.
    if (width <= 0 || height <= 0) {
      return;
    }
    const prevW = this.width;
    const prevH = this.height;
    this.width = width;
    this.height = height;
    // Respond to a resize by rescaling every node proportionally to the new
    // bounds (about the origin, which maps the old centre to the new centre).
    // Clamping alone would just pin outliers to the edges — the graph would sit
    // off-centre and only slowly drift back via gravity. Scaling keeps its
    // composition and keeps it centred immediately as the panel is dragged.
    if (prevW > 0 && prevH > 0 && (prevW !== width || prevH !== height)) {
      const sx = width / prevW;
      const sy = height / prevH;
      for (const n of this.nodes) {
        n.x *= sx;
        n.y *= sy;
      }
    }
    // Pull any node still outside the (possibly smaller) bounds back inside.
    const pad = 14;
    for (const n of this.nodes) {
      n.x = Math.max(pad, Math.min(width - pad, n.x));
      n.y = Math.max(pad + 6, Math.min(height - pad, n.y));
    }
  }

  reset(): void {
    this.nodes.length = 0;
    this.edges.length = 0;
    this.nextId = 0;
  }

  counts(): GraphCounts {
    let active = 0;
    for (const n of this.nodes) {
      if (n.active) {
        active += 1;
      }
    }
    return { nodes: this.nodes.length, edges: this.edges.length, active };
  }

  addMemory(
    label: string,
    type: MemType,
    links: MemLink[] = [],
    detail = '',
    recordId?: string,
  ): number {
    // Idempotent on record id: a memory-write append for a record already on
    // the graph (e.g. a redelivered frame) returns the existing node.
    if (recordId !== undefined) {
      const existing = this.nodes.find((n) => n.recordId === recordId);
      if (existing !== undefined) {
        return existing.id;
      }
    }
    const tier = TYPE[type].tier;
    const cx = this.width / 2;
    const cy = this.height / 2;
    const ang = (this.nextId * 2.399) % (Math.PI * 2);
    const r = 30 + Math.random() * 60;
    const node: MemNode = {
      id: this.nextId++,
      label,
      type,
      tier,
      detail,
      x: cx + Math.cos(ang) * r + (Math.random() - 0.5) * 20,
      y: cy + Math.sin(ang) * r + (Math.random() - 0.5) * 20,
      vx: 0,
      vy: 0,
      born: performance.now(),
      radius: TIER_RADIUS[tier] + Math.random() * 1.5,
      pulse: 0,
      active: false,
      ...(recordId !== undefined ? { recordId } : {}),
    };
    this.nodes.push(node);
    for (const link of links) {
      const tid = typeof link === 'number' ? link : link.to;
      const kind = typeof link === 'number' ? 'derives' : link.kind;
      if (this.nodes.some((m) => m.id === tid)) {
        this.edges.push({ a: node.id, b: tid, kind, born: performance.now() });
      }
    }
    return node.id;
  }

  setNodeText(id: number, label: string, detail: string): void {
    const n = this.nodes.find((m) => m.id === id);
    if (n) {
      n.label = label;
      n.detail = detail;
    }
  }

  setContext(ids: number[]): void {
    const set = new Set(ids);
    for (const n of this.nodes) {
      n.active = set.has(n.id);
    }
  }

  activate(ids: number[]): void {
    for (const id of ids) {
      const n = this.nodes.find((m) => m.id === id);
      if (n) {
        n.active = true;
      }
    }
  }

  clearContext(): void {
    for (const n of this.nodes) {
      n.active = false;
    }
  }

  /** Remove every node matching `shouldRemove`, then prune any edge that
   *  touched a removed node. Mutates the arrays in place (their identity is
   *  held by the render loop). */
  private removeNodes(shouldRemove: (n: MemNode) => boolean): void {
    const removed = new Set<number>();
    for (const n of this.nodes) {
      if (shouldRemove(n)) {
        removed.add(n.id);
      }
    }
    if (removed.size === 0) {
      return;
    }
    const nodes = this.nodes.filter((n) => !removed.has(n.id));
    this.nodes.splice(0, this.nodes.length, ...nodes);
    const edges = this.edges.filter((e) => !removed.has(e.a) && !removed.has(e.b));
    this.edges.splice(0, this.edges.length, ...edges);
  }

  removeRecords(recordIds: readonly string[]): void {
    if (recordIds.length === 0) {
      return;
    }
    const ids = new Set(recordIds);
    this.removeNodes((n) => n.recordId !== undefined && ids.has(n.recordId));
  }

  retainRecords(keep: ReadonlySet<string>): void {
    this.removeNodes((n) => n.recordId !== undefined && !keep.has(n.recordId));
  }

  clearMemoryRecords(): void {
    this.removeNodes((n) => n.recordId !== undefined);
  }

  /** Advance the force simulation one step (repulsion + gravity + springs). */
  step(): void {
    const cx = this.width / 2;
    const cy = this.height / 2;
    const { nodes, edges } = this;

    // Ease repulsion off once the graph outgrows REPULSION_FULL_N nodes (see the
    // constant). At full strength a long session's nodes can't fit the small
    // canvas, so the cloud churns against the bounds forever; scaling lets it
    // settle. Small graphs keep the original full-strength feel.
    const repulsion =
      REPULSION * Math.min(1, REPULSION_FULL_N / Math.max(1, nodes.length));

    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i];
      for (let j = i + 1; j < nodes.length; j++) {
        const b = nodes[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d2 = dx * dx + dy * dy;
        // Coincident (or near-coincident) nodes: `dx/d` would be 0, so the usual
        // repulsion has no direction and the pair stays fused forever. Push them
        // apart along a deterministic per-pair angle instead so they separate.
        let ux: number;
        let uy: number;
        if (d2 < 0.01) {
          const ang = i * 1.7 + j * 0.9;
          ux = Math.cos(ang);
          uy = Math.sin(ang);
        } else {
          const d = Math.sqrt(d2);
          ux = dx / d;
          uy = dy / d;
        }
        // Floor the denominator at MIN_D2 so close-range repulsion is bounded
        // (≤ repulsion / MIN_D2) — the fix for the corner-strobe velocity spike.
        const force = repulsion / Math.max(d2, MIN_D2);
        const fx = ux * force;
        const fy = uy * force;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }
      a.vx += (cx - a.x) * 0.0016;
      a.vy += (cy - a.y) * 0.0016;
    }

    for (const e of edges) {
      const a = nodes.find((n) => n.id === e.a);
      const b = nodes.find((n) => n.id === e.b);
      if (!a || !b) {
        continue;
      }
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const f = (d - 64) * 0.012;
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }

    const pad = 14;
    for (const n of nodes) {
      n.vx *= 0.86;
      n.vy *= 0.86;
      // Hard speed ceiling — the anti-strobe backstop. Even if a force momentarily
      // spikes, no node can travel more than MAX_SPEED px in a frame, so it can
      // never teleport across the canvas or flicker between the corners.
      const speed = Math.hypot(n.vx, n.vy);
      if (speed > MAX_SPEED) {
        const k = MAX_SPEED / speed;
        n.vx *= k;
        n.vy *= k;
      }
      n.x += n.vx;
      n.y += n.vy;
      n.x = Math.max(pad, Math.min(this.width - pad, n.x));
      n.y = Math.max(pad + 6, Math.min(this.height - pad, n.y));
      if (n.pulse < 1) {
        n.pulse = Math.min(1, n.pulse + 0.04);
      }
    }
  }

  nodeAt(px: number, py: number): MemNode | null {
    // Iterate last-to-first so newest (top-drawn) nodes win.
    for (let i = this.nodes.length - 1; i >= 0; i--) {
      const n = this.nodes[i];
      const r = n.radius + 9;
      if ((px - n.x) ** 2 + (py - n.y) ** 2 < r * r) {
        return n;
      }
    }
    return null;
  }

  /** Render the current frame. `focus` is the hovered/selected node, if any. */
  draw(ctx: CanvasRenderingContext2D, now: number, focus: MemNode | null): void {
    const { nodes, edges } = this;
    ctx.clearRect(0, 0, this.width, this.height);

    for (const e of edges) {
      const a = nodes.find((n) => n.id === e.a);
      const b = nodes.find((n) => n.id === e.b);
      if (!a || !b) {
        continue;
      }
      const age = Math.min(1, (now - e.born) / 600);
      const style = EDGE[e.kind];
      const inCtx = a.active && b.active;
      const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
      grad.addColorStop(0, TYPE[a.type].color);
      grad.addColorStop(1, TYPE[b.type].color);
      ctx.strokeStyle = grad;
      ctx.globalAlpha = (inCtx ? style.alpha * 2.6 : style.alpha) * age;
      ctx.lineWidth = inCtx ? 1.5 : 1;
      ctx.setLineDash(style.dash);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Travelling pulse only on causal "derives" edges currently in context.
      if (e.kind === 'derives' && inCtx) {
        const t = (now / 1200 + (e.a + e.b) * 0.13) % 1;
        const px = a.x + (b.x - a.x) * t;
        const py = a.y + (b.y - a.y) * t;
        ctx.globalAlpha = 0.7 * age;
        ctx.fillStyle = TYPE[b.type].color;
        ctx.beginPath();
        ctx.arc(px, py, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);

    for (const n of nodes) {
      const col = TYPE[n.type].color;
      const r = n.radius * (0.4 + 0.6 * n.pulse);
      const isFocus = n === focus;
      const lit = n.active || isFocus;

      if (n.active) {
        ctx.globalAlpha = 0.85;
        ctx.strokeStyle = col;
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 7 + Math.sin(now / 400 + n.id) * 1.5, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      ctx.globalAlpha = (lit ? 0.3 : 0.09) + (n.active ? 0.12 * Math.sin(now / 500 + n.id) : 0);
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(n.x, n.y, r + (isFocus ? 10 : 6), 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = lit ? 1 : 0.38;
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#05060a';
      ctx.beginPath();
      ctx.arc(n.x, n.y, r * 0.42, 0, Math.PI * 2);
      ctx.fill();

      const showLabel = isFocus || n.active || now - n.born < 3500 || nodes.length <= 10;
      if (showLabel) {
        ctx.globalAlpha = lit ? 1 : 0.5;
        ctx.fillStyle = lit ? '#e6e8ee' : '#7d8290';
        ctx.font = "10px 'JetBrains Mono', monospace";
        ctx.textAlign = 'center';
        ctx.fillText(n.label, n.x, n.y - r - 7);
      }
    }
    ctx.globalAlpha = 1;
  }

  /** Edge-kind summary for a node, e.g. "2 derives · 1 mentions". */
  linkSummary(node: MemNode): string {
    const kinds: Partial<Record<EdgeKind, number>> = {};
    for (const e of this.edges) {
      if (e.a === node.id || e.b === node.id) {
        kinds[e.kind] = (kinds[e.kind] ?? 0) + 1;
      }
    }
    const parts = Object.entries(kinds).map(([k, v]) => `${v} ${k}`);
    return parts.join(' · ') || 'no links yet';
  }
}
